import { ApplicationMglInformationModel } from '../models/transport/ApplicationMglInformation';
import { MembershipSelectionType } from '../models/view/MembershipSelectionType';
import { CalculatorFormModel } from '../models/view/CalculatorForm';
import { MglCustomerModel } from '../models/transport/MglCustomer';
import { BasicPersistenceService } from './BasicPersistenceService';

/**
 * This class is used for communication between (insurance) applications and the membership application form (1-stufigen).
 * The calling application can pass information to and get customer data from the membership application form.
 * The membership application form can get application information from and pass customer data to the calling application.
 *
 */
export class BasicMglConnectionService<T extends BasicAppDefinition> {
	protected readonly applicationPersistenceService: BasicPersistenceService<T>;
	protected readonly customerStorageKey: string;
	protected readonly applicationInformationStorageKey: string;
	protected readonly options: BasicAppOptions<T>;

	/**
	 * Creates an instance of BasicMglConnectionService.
	 * @param options - application options.
	 */
	constructor(options: BasicAppOptions<T>) {
		this.options = options;
		this.applicationInformationStorageKey = 'applicationMglInformation';
		this.customerStorageKey = 'mglCustomer';
		this.applicationPersistenceService = new BasicPersistenceService(options);
	}

	/**
	 * Gets stored information about the application calling the membership form application (1-stufigen).
	 * It is ensured, that only application mgl information that is targeted for the calling application is returned.
	 *
	 * @return application information for membership application or empty application information (check with Valid getter).
	 */
	public retrieveApplicationInformation(): ApplicationMglInformationModel {
		const dataObject = T.Utils.Store.get(this.applicationInformationStorageKey, T.Utils.Store.SESSION),
			applicationMglInformation = ApplicationMglInformationModel.createFromObject(dataObject),
			applicationModel = this.applicationPersistenceService.getApplicationModel();

		if (applicationModel && applicationModel.Register && applicationModel.Register === applicationMglInformation.ApplicationName) {
			return applicationMglInformation;
		}
		return new ApplicationMglInformationModel();
	}

	/**
	 * Stores relevant information from the provided calculator model for the membership form application (1-stufigen).
	 *
	 * @param calculatorModel - the calculator model.
	 * @param custom - dictionary object with key value pairs to additionally provide to the membership form.
	 */
	public storeApplicationInformation(calculatorModel: CalculatorFormModel, custom?: { [key: string]: number | string }): void {
		const applicationMglInformation = new ApplicationMglInformationModel();
		applicationMglInformation.config.membership = this.getMembershipForMembershipSelection(calculatorModel.MembershipSelection);
		applicationMglInformation.config.Geburtsdatum = calculatorModel.Geburtsdatum;
		applicationMglInformation.config.membershipadvertisingkey = this.options.app.membershipadvertisingkey;
		applicationMglInformation.config.processUrl = window.location.href;
		applicationMglInformation.input.VersicherungsBeginn = calculatorModel.Versicherungsbeginn;
		applicationMglInformation.input.ProductKey = calculatorModel.ProductKey;
		applicationMglInformation.input.ProduktWerbeschluessel = this.options.app.advertisingkey;
		applicationMglInformation.input.FormularId = this.generateFormularId(this.options.name);
		applicationMglInformation.ApplicationName = this.options.name;
		applicationMglInformation.custom = custom || {};

		T.Utils.Store.set(this.applicationInformationStorageKey, applicationMglInformation, T.Utils.Store.SESSION);
	}

	/**
	 * Updates the stored application information with the provided application information, if the provided application information is valid.
	 *
	 * @param applicationMglInformation - application information for membership application.
	 */
	public updateApplicationInformation(applicationMglInformation: ApplicationMglInformationModel): void {
		if (applicationMglInformation && applicationMglInformation.Valid) {
			T.Utils.Store.set(this.applicationInformationStorageKey, applicationMglInformation, T.Utils.Store.SESSION);
		}
	}

	protected getMembershipForMembershipSelection(membershipSelection: MembershipSelectionType | number): number {
		if (!isNaN(membershipSelection as number)) {
			// this is for backward compatibility, if the calling application does not use the MembershipSelectionType,
			// but directly provides the membership value
			return Number(membershipSelection);
		}
		return MembershipSelectionType.getMembershipCode(membershipSelection as string);
	}

	protected generateFormularId(applicationName: string): string {
		let formularId = 'sample';

		if (applicationName) {
			// only keep all upper case letters to create a short string that is not rejected by the api
			formularId = applicationName.replace(/Application/g, '').replace(/[a-z]/g, '');
		}
		return `${formularId.toLowerCase()}MglForm`;
	}

	protected clearApplicationMglInformation(): void {
		T.Utils.Store.set(this.applicationInformationStorageKey, null, T.Utils.Store.SESSION);
	}

	/**
	 * Gets the persisted customer data entered by the user on the membership application form.
	 * It is ensured, that only customer data that is targeted for the calling application is returned.
	 *
	 * @return customer data or empty customer (check with Valid getter).
	 */
	public retrieveCustomer(): MglCustomerModel {
		const dataObject = T.Utils.Store.get(this.customerStorageKey, T.Utils.Store.SESSION),
			customerModel = MglCustomerModel.createFromObject(dataObject);

		if (this.options.name === customerModel.TargetApplicationName) {
			return customerModel;
		} else {
			if (customerModel.Valid) {
				// remove customer only, if a valid customer for the wrong target application exists
				this.clearCustomer();
			}
			return new MglCustomerModel();
		}
	}

	/**
	 * Persists the customer data entered by the user on the membership application form.
	 * Removes the stored application information, so that it is not reused for the wrong membership application form.
	 *
	 * @param customerModel - value customer data.
	 */
	public storeCustomer(customerModel: MglCustomerModel): void {
		const applicationMglInformation = this.retrieveApplicationInformation();

		if (applicationMglInformation) {
			customerModel.TargetApplicationName = applicationMglInformation.ApplicationName;
			customerModel.membership = applicationMglInformation.config.membership;
			this.clearApplicationMglInformation();

			T.Utils.Store.set(this.customerStorageKey, customerModel, T.Utils.Store.SESSION);
		}
	}

	protected clearCustomer(): void {
		T.Utils.Store.set(this.customerStorageKey, null, T.Utils.Store.SESSION);
	}

	/**
	 * Clears all connection data between 1-stufigen and the calling application.
	 */
	public clearConnectionSession(): void {
		this.clearApplicationMglInformation();
		this.clearCustomer();
	}
}
